/**
 * TanStack Query Client Configuration
 *
 * Centralized query client with:
 * - Sensible defaults for stale time and cache
 * - WebSocket-driven cache invalidation
 * - Query key factory for consistency
 * - Debounced invalidation for bulk operations
 */

import { isArtifactEntityType } from '@bower/shared/constants/artifact';
import {
  isBackgroundTaskActive,
  readBackgroundTasks,
} from '@bower/shared/constants/background-task';
import type { WorkspaceItemsQuery } from '@bower/shared';
import { QueryClient } from '@tanstack/react-query';
import { refreshFolderLists } from './folderListRefresh';
import { invalidateJobStateQueries, PROTOCOL_RUN_ENTITY_TYPE } from './jobStateInvalidation';
import { useCanvasClipboardStore } from '../stores/canvasClipboardStore';
import { useSelectionStore } from '../stores/selectionStore';

export const ONBOARDING_CHECKLIST_KEY = ['onboarding-checklist'] as const;
/** The structured protocol: current version, its blobs and its steps. */
export const STRUCTURED_PROTOCOL_KEY = ['structured-protocol'] as const;
/** A protocol's version history. Its own root rather than a child of the key
 * above, so invalidating one protocol's rows does not refetch every open
 * history and vice versa (rules/react.md on prefix matching). */
export const PROTOCOL_VERSIONS_KEY = ['protocol-versions'] as const;
/**
 * Which protocols in the workspace are still importing, for the library's chips.
 *
 * Registered here rather than left a literal inside its own hook because
 * `useDeriveProtocol` and the create hooks invalidate it from other modules.
 */
/**
 * KEYED BY WORKSPACE, because the answer is entirely workspace-specific and the
 * cache is not cleared when somebody switches.
 *
 * Without the id in the key, switching workspaces serves the previous
 * workspace's import ids to the new one's library for the whole stale window:
 * chips appear against protocols that are not importing here, and worse, the
 * ids themselves are data about another workspace's contents.
 * The switch does not remount the app, so nothing else would have corrected it.
 *
 * Callers that INVALIDATE pass the same workspace they read with. Prefix
 * matching means passing the root alone would also work, and would also
 * invalidate every other workspace's cached answer - which is not wrong, just
 * wasteful, so the id is threaded through rather than dropped.
 */
export const protocolImportsKey = (workspaceId: string | null | undefined) =>
  ['protocol-imports', { workspaceId: workspaceId ?? null }] as const;

// Protocol run reads (spec 2026-10-15 protocol runs layout). Keyed by
// workspace for the reason `protocolImportsKey` records. Each is its own
// root so invalidating one protocol's run list does not refetch the counts,
// and vice versa.
/** Run and open-run counts for one page of the Protocols list. The ids are
 * sorted so the same page in another order shares one cache entry. */
export const protocolRunCountsKey = (workspaceId: string | null, ids: readonly string[]) =>
  ['protocol-run-counts', { workspaceId, ids: [...ids].sort() }] as const;
/** One protocol's runs, newest first, for one version, earlier runs, or all. */
export const protocolRunsKey = (
  workspaceId: string | null,
  protocolId: string,
  version: number | 'earlier' | 'all',
) => ['protocol-runs', { workspaceId, protocolId, version }] as const;
/** One protocol's run count per version. */
export const protocolRunVersionCountsKey = (workspaceId: string | null, protocolId: string) =>
  ['protocol-run-version-counts', { workspaceId, protocolId }] as const;
export { PROTOCOL_RUN_ENTITY_TYPE };
/** One run record. */
export const protocolRunKey = (workspaceId: string | null, protocolId: string, runId: string) =>
  ['protocol-run', { workspaceId, protocolId, runId }] as const;

// Debounce configuration for bulk operations
const INVALIDATION_DEBOUNCE_MS = 150; // Wait 150ms to batch invalidations

/**
 * The parent fields a /sync payload may carry, as the backend's one payload
 * builder writes them (audit.extension.ts `syncPayload`). `parentId` is the
 * parent now (a project for a collection; the parent entity for a file, typed
 * by `parentEntityType`); `null` means the workspace root and `undefined` that
 * the event did not say. A move also names where it came from:
 * `previousParentId` (+ `previousParentEntityType` / `previousProjectId` for a
 * file).
 */
export interface EntityChangeData {
  parentId?: string | null;
  projectId?: string;
  workspaceId?: string;
  parentEntityType?: string;
  previousParentId?: string | null;
  previousParentEntityType?: string;
  previousProjectId?: string;
  /** A `protocol_run` change's protocol (`lib/jobStateInvalidation.ts`). */
  protocolId?: string;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const strOrNull = (v: unknown): string | null | undefined => (v === null ? null : str(v));

/** Reads the parent fields off a raw /sync payload, keeping root (`null`) apart from absent. */
export function toEntityChangeData(
  raw: Record<string, unknown> | undefined,
  workspaceId: string,
): EntityChangeData {
  const d = raw ?? {};
  return {
    parentId: strOrNull(d.parentId),
    projectId: str(d.projectId),
    workspaceId,
    parentEntityType: str(d.parentEntityType),
    previousParentId: strOrNull(d.previousParentId),
    previousParentEntityType: str(d.previousParentEntityType),
    previousProjectId: str(d.previousProjectId),
    protocolId: str(d.protocolId),
  };
}

// Pending invalidations (batched to reduce query refetches during bulk operations)
interface PendingInvalidation {
  entityType: string;
  changeType: 'created' | 'updated' | 'deleted';
  entityId: string;
  data?: EntityChangeData;
}

let pendingInvalidations: PendingInvalidation[] = [];
let invalidationTimer: ReturnType<typeof setTimeout> | null = null;

// Query keys factory for consistent cache management
export const queryKeys = {
  // Workspace queries
  workspaces: {
    all: ['workspaces'] as const,
    // Deliberately a SEPARATE ROOT — same failure as artifacts.detail below.
    // Under ['workspaces', id] every cached workspace detail was a prefix-child
    // of the bare ['workspaces'] list key, so all FIVE broad sweeps of it
    // refetched EVERY cached workspace detail:
    //   1. executeBatchedInvalidations' workspace branch (below) — which also
    //      ran a per-entity loop right after it, so the sweep was redundant
    //      there as well. Live since 2026-10-23: the ORCID import status
    //      writes (`recordOrcidImportStatus`, `queueOrcidPublicationsImport`)
    //      broadcast a `workspace` update, at most a few per import.
    //   2-3. TransferOwnershipDialog, transfer + delete.
    //   4. VerifyEmail's post-verify cache reset.
    //   5. useOnboardingInvitationCheck's invitation gate.
    // Only 1-3 need the changed detail and now name it explicitly; 4 and 5 are
    // list-only by design (they run before a workspace is pinned, so no detail
    // is cached). Re-audit this list when adding a sweep — an undercount here
    // reads as "every site is handled".
    detail: (id: string) => ['workspace-detail', id] as const,
    // Own root: the caller's workspaces scheduled for deletion. Invalidated
    // explicitly by schedule/restore, never by a broad ['workspaces'] sweep.
    pendingDeletions: ['workspace-pending-deletions'] as const,
    // Its own root, for the reason `detail` gives: a workspace sweep must not
    // refetch logo bytes. Keyed by hash, so a new logo is a new entry.
    logo: (workspaceId: string, logoHash: string) =>
      ['workspace-logo', workspaceId, logoHash] as const,
    // The key the members list is ACTUALLY registered under (useWorkspaceUsers).
    // It is not ['workspaces', id, 'members'] — that shape was declared here but
    // no query ever used it, so the invalidation in the `user` branch below was a
    // silent no-op and a profile edit never refreshed the members list.
    members: (workspaceId: string) => ['workspaceUsers', { workspaceId }] as const,
    // Same roster with each member's role and support-seat flag (the
    // transfer-ownership picker needs both). Under the same
    // ['workspaceUsers'] root, so every sweep of that root refreshes it too.
    membersWithRoles: (workspaceId: string) =>
      ['workspaceUsers', { workspaceId, withRoles: true }] as const,
  },

  // User queries
  users: {
    all: ['users'] as const,
    // Own root, per the detail-vs-list rule pinned in queryClient.test.ts.
    // Nothing sweeps ['users'] broadly today, so this is prophylactic — but it
    // also decouples detail('current') from `current` below, which did collide.
    detail: (id: string) => ['user-detail', id] as const,
    current: ['users', 'current'] as const,
  },

  // Project queries
  projects: {
    // The collection lists (`useInfiniteProjects`); each list appends its
    // filters, so this is the prefix that invalidates every one of them.
    list: (workspaceId: string | null) => ['projects', 'infinite', { workspaceId }] as const,
    detail: (id: string) => ['projects', id] as const,
    children: (projectId: string) => ['projects', projectId, 'children'] as const,
  },

  // The suggested collections (decision 0018): which collection each purpose resolves to.
  purposeCollections: (workspaceId: string | null | undefined) =>
    ['purpose-collections', workspaceId ?? ''] as const,

  // Artifact queries (notes, attachments, etc.)
  artifacts: {
    all: (parentType: string, parentId: string) => ['artifacts', { parentType, parentId }] as const,
    // Deliberately a SEPARATE ROOT, not an ['artifacts', …] child.
    //
    // Under the old ['artifacts', id] shape every single-artifact query was a
    // prefix-child of the bare ['artifacts'] key, so every broad
    // `invalidateQueries({ queryKey: ['artifacts'] })` call site (8 of them at
    // the time) refetched EVERY mounted artifact detail at once. On 2026-08-03 that tripped the
    // mass-artifact-egress security alert: 822 reads of just 16 distinct ids
    // in 14 minutes from one session, at up to 239 req/min — a client refetch
    // storm that read as bulk exfiltration.
    //
    // Adding a segment does NOT fix this: TanStack matches by PREFIX, so
    // ['artifacts','detail',id] still matches ['artifacts']. Only a distinct
    // root decouples the two. Lists stay under ['artifacts'] so the broad
    // sweeps keep doing their job; details are refreshed explicitly by the
    // mutation / restore path that changed them.
    detail: (id: string) => ['artifact-detail', id] as const,
  },

  // Equipment. An equipment item IS an Artifact (artifactType = EQUIPMENT), but
  // it has its own REST surface and its own hooks, so it keys separately from
  // the artifact families above.
  equipment: {
    all: (workspaceId: string | null) => ['equipment', { workspaceId }] as const,
    // Deliberately a SEPARATE ROOT, for exactly the reason spelled out on
    // artifacts.detail above. Under ['equipment', id, …] every cached equipment
    // detail was a prefix-child of the bare ['equipment'] list key, so all THREE
    // broad sweeps of that root refetched every one of them:
    //   1. The artifact branch of executeBatchedInvalidations (below) — which
    //      fires on EVERY artifact / note / attachment socket batch, the hottest
    //      invalidation path in the app. It runs a per-entity detail loop right
    //      beside the sweep, so the sweep was redundant there too.
    //   2. The workspace drawer's Equipment tab refresh.
    //   3. EquipmentDetail's trash-delete + undo.
    // All three are list-only now; 1 and 3 name the changed detail explicitly,
    // and 2 is list-only by design. Re-audit this list when adding a sweep — an
    // undercount here reads as "every site is handled".
    //
    // Found during the review of the workspaces/users/conversations move
    // (#1134) and left out of scope there.
    //
    // No `{ workspaceId }` segment, unlike `all` above: every sibling detail key
    // is id-only, and equipment ids are workspace-scoped UUIDs, so two
    // workspaces cannot collide on one.
    //
    // The cache-clear safety net is NARROWER than "a workspace switch always
    // clears": switchWorkspace calls clearWorkspaceQueries, and the
    // auth-transition layout effect clears
    // on page load — but `setWorkspace()` (WorkspaceContext) does NOT. Its two
    // callers (init auto-select, and the auto-recovery effect when the pinned
    // workspace leaves the membership list) can only run with an already-empty
    // cache today, so nothing is addressable across a boundary — but that is a
    // property of what currently invalidates ['workspaces'], not a guarantee.
    // A new setWorkspace caller, or a new mid-session ['workspaces']
    // invalidation, makes this reachable for EVERY id-only detail family
    // (artifacts, workspaces, users, conversations, snapshots, equipment) —
    // fix it there, not by re-adding a segment here.
    detail: (id: string) => ['equipment-detail', id] as const,
  },

  // Hierarchy tree queries
  hierarchy: {
    root: (workspaceId: string) => ['hierarchy', 'root', { workspaceId }] as const,
    children: (parentType: string, parentId: string) =>
      ['hierarchy', 'children', { parentType, parentId }] as const,
    sharedWithMe: (workspaceId: string) =>
      ['hierarchy', 'shared-with-me', { workspaceId }] as const,
  },

  // Per-user sidebar pins — keyed by BOTH workspace and user so a workspace
  // switch or account switch never bleeds one user's pins into another's UI.
  pins: {
    list: (workspaceId: string, userId: string) => ['pins', { workspaceId, userId }] as const,
  },

  // Conversations
  conversations: {
    all: (workspaceId: string) => ['conversations', { workspaceId }] as const,
    // Own root — see users.detail above. Prophylactic only: this whole family is
    // currently dead (chat registers ['session-history', …] instead), and there
    // is no bare ['conversations'] sweep. `messages` stays put as a LIST, but
    // note nothing would reach it today either — conversations.all is
    // ['conversations', {workspaceId}], which cannot prefix-match
    // ['conversations', id, 'messages'].
    detail: (id: string) => ['conversation-detail', id] as const,
    messages: (id: string) => ['conversations', id, 'messages'] as const,
  },

  // Search
  search: {
    results: (workspaceId: string, query: string, entityTypes?: string[]) =>
      ['search', { workspaceId, query, entityTypes }] as const,
  },

  // Linked entities (relationships)
  linkedEntities: {
    forEntity: (entityType: string, entityId: string, workspaceId?: string) =>
      ['linked-entities', { entityType, entityId, workspaceId }] as const,
  },

  // Billing
  billing: {
    plan: (workspaceId: string) => ['billing', 'plan', workspaceId] as const,
    invitations: (workspaceId: string) => ['billing', 'invitations', workspaceId] as const,
  },

  // AI quota — per-feature usage state for the Billing tab + 80% warning.
  aiQuota: {
    me: (workspaceId: string) => ['ai-quota', 'me', workspaceId] as const,
  },

  // Version history snapshots
  snapshots: {
    list: (artifactId: string) => ['snapshots', 'list', artifactId] as const,
    // Own root, like the details above. No bare ['snapshots'] sweep exists today
    // — this keeps the family compliant so the guard needs no exception for it.
    detail: (snapshotId: string) => ['snapshot-detail', snapshotId] as const,
  },

  // Entity-level ACL — Share dialog state per entity.
  entityAcl: {
    access: (entityType: string, entityId: string) =>
      ['entityAcl', 'access', { entityType, entityId }] as const,
    snapshot: (entityType: string, entityId: string) =>
      ['entityAcl', 'snapshot', { entityType, entityId }] as const,
    /**
     * Restricted-info payload for the "you don't have access" splash.
     * 404 → returned as null by the hook; 200 → owner + title.
     */
    restrictedInfo: (entityType: string, entityId: string) =>
      ['entityAcl', 'restrictedInfo', { entityType, entityId }] as const,
    /**
     * Per-workspace ACL enforcement flag state.  Used to gate UI
     * surfaces (Share dialog, lock badges, "Shared with me") so we
     * don't show controls that have no effect when ops hasn't flipped
     * the flag on for this workspace.
     */
    featureFlags: (workspaceId: string) => ['entityAcl', 'featureFlags', { workspaceId }] as const,
  },

  // The unified workspace view (folders + files). `params.parent` is the
  // folder id, or 'workspace' for the root, so `['workspace-items',
  // { workspaceId }, { parent }]` partially matches every sort/filter variant
  // of one folder's list: that is the key the /sync path invalidates.
  workspaceItems: {
    list: (workspaceId: string, params: WorkspaceItemsListParams) =>
      ['workspace-items', { workspaceId }, params] as const,
  },
  // The Type menu's per-kind counts, on their own root (never swept with the
  // lists they describe). `surface` + `params` are the list's identity minus
  // its Type filter, so ticking a kind keeps the same counts.
  kindCounts: (workspaceId: string, surface: 'workspace' | 'trash', params: object) =>
    ['kind-counts', { workspaceId }, surface, params] as const,
  // The breadcrumb and the card preview live on their OWN roots, so a sweep of
  // folder lists never refetches every mounted breadcrumb or preview (the
  // detail-vs-list rule above).
  folderPath: (workspaceId: string, folderId: string) =>
    ['folder-path', { workspaceId, folderId }] as const,
  itemPreview: (id: string) => ['item-preview', id] as const,
  // One window of grid thumbnails. `versions` is `id@updatedAt` per file, so a
  // replaced file signs afresh without any /sync wiring of its own.
  itemThumbnails: (workspaceId: string, versions: readonly string[]) =>
    ['item-thumbnails', { workspaceId }, versions] as const,
};

/** A workspace items list's identity: the whole query except the page cursor and size. */
export type WorkspaceItemsListParams = Omit<WorkspaceItemsQuery, 'cursor' | 'limit'>;

/** `params.parent` of the workspace root's list. */
export const WORKSPACE_ROOT_PARENT = 'workspace';

// staleTime targets by data volatility. Applied centrally via
// setQueryDefaults below so individual hooks don't need to think about it.
// WebSocket-driven invalidation (see invalidateEntityQueries) keeps data
// fresh inside these windows — the staleTime just prevents redundant
// network fetches on back-navigation / remount.
const MINUTE_MS = 60 * 1000;
export const STALE_TIME = {
  /** Default for anything not explicitly tiered. */
  DEFAULT: 30 * 1000,
  /** Rarely changes; user-driven invalidation covers it. */
  WORKSPACE: 5 * MINUTE_MS,
  /** Often-touched entities; WebSocket events cover freshness. */
  ENTITY: 2 * MINUTE_MS,
} as const;

// Create the query client with sensible defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME.DEFAULT,
      // Cache data for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry once. The old value (2) meant a slow/failing endpoint cost 3×
      // its latency before surfacing, and every invalidation storm multiplied
      // it. One retry still absorbs transient blips.
      //
      // DO NOT make this skip retries for "structured server answers" (403 /
      // 404 / 409) on the theory that the server returns the same answer
      // twice. It doesn't always: the api.service 401 interceptor deliberately
      // rejects a PRE-AUTH 401 quietly — no Authorization header yet, Firebase
      // still hydrating — precisely so THIS retry recovers it once auth is
      // ready (pinned by api.service.test.ts "pre-auth race"). A code-aware
      // guard was tried and reverted for exactly that reason: the second ask
      // carries a token, so it is not the same request. Any future attempt has
      // to special-case the auth race first.
      retry: 1,
      // NO global focus-refetch. Freshness is WebSocket-driven
      // (invalidateEntityQueries below) — refetching every active query on
      // every tab-return re-fetched the whole screen across a ~500ms-RTT
      // link for data the socket already keeps fresh. The few families whose
      // sources of change are OUTSIDE the socket path (Stripe billing state,
      // AI quota consumed by background jobs) opt back in via
      // setQueryDefaults below.
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect (WebSocket handles this)
      refetchOnReconnect: false,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
    },
  },
});

// Per-entity staleTime overrides — applied by queryKey prefix so every hook
// that uses `queryKeys.workspaces.*` / `queryKeys.projects.*` etc. inherits
// the right value without restating it.
queryClient.setQueryDefaults(['workspaces'], { staleTime: STALE_TIME.WORKSPACE });
queryClient.setQueryDefaults(['projects'], { staleTime: STALE_TIME.ENTITY });
queryClient.setQueryDefaults(['artifacts'], { staleTime: STALE_TIME.ENTITY });
// Artifact details live on their own root (see queryKeys.artifacts.detail), so
// they need the ENTITY staleTime restated — the ['artifacts'] prefix above no
// longer reaches them. Dropping this silently returns every open note to the
// 30s global default.
queryClient.setQueryDefaults(['artifact-detail'], { staleTime: STALE_TIME.ENTITY });
// Same restatement for workspace details, which moved off ['workspaces'] for the
// same reason. WORKSPACE, not ENTITY — it must match the ['workspaces'] line
// above, or a workspace detail and the list it came from expire on different
// clocks. (users/conversations details need no line here: neither root ever had
// a staleTime default, so moving them changes nothing.)
queryClient.setQueryDefaults(['workspace-detail'], { staleTime: STALE_TIME.WORKSPACE });

// Families whose data changes server-side WITHOUT a corresponding WebSocket
// event. A returning tab must refetch these to pick up out-of-band changes —
// they are NOT covered by invalidateEntityQueries.
//   - billing: Stripe webhooks mutate it.
//   - ai-quota: background AI jobs consume it.
//   - shareLinks: view counts tick server-side.
//   - entityAcl: another workspace member can grant/restrict/downgrade access;
//     there is no socket event for ACL changes, so without focus-refetch the
//     lock icon / "View only" badge / disabled controls stay stale until the
//     page remounts. (useAclFeatureFlags already sets this per-hook; this
//     covers its access/snapshot/restrictedInfo siblings too.)
//   - auditLogs / entityAuditLogs: a continuous feed of OTHER members' actions
//     with no socket event to the viewer. refetchOnMount:'always' covers
//     navigating in; focus-refetch covers a tab left open on the feed.
queryClient.setQueryDefaults(['billing'], { refetchOnWindowFocus: true });
queryClient.setQueryDefaults(['ai-quota'], { refetchOnWindowFocus: true });
queryClient.setQueryDefaults(['shareLinks'], { refetchOnWindowFocus: true });
queryClient.setQueryDefaults(['entityAcl'], { refetchOnWindowFocus: true });
queryClient.setQueryDefaults(['auditLogs'], { refetchOnWindowFocus: true });
queryClient.setQueryDefaults(['entityAuditLogs'], { refetchOnWindowFocus: true });

/**
 * Invalidate queries when WebSocket entity change events are received
 * Called from SocketContext when sync events arrive
 *
 * This is the core of our real-time sync - WebSocket events trigger
 * cache invalidation, TanStack Query automatically refetches,
 * and all subscribed components update.
 *
 * Uses debouncing to batch rapid invalidations (e.g., bulk operations
 * that trigger 29 separate WebSocket events).
 */
export function invalidateEntityQueries(
  entityType: string,
  changeType: 'created' | 'updated' | 'deleted',
  entityId: string,
  data?: EntityChangeData,
): void {
  // Add to pending batch
  pendingInvalidations.push({ entityType, changeType, entityId, data });

  // Clear existing timer
  if (invalidationTimer) {
    clearTimeout(invalidationTimer);
  }

  // Schedule batch execution
  invalidationTimer = setTimeout(() => {
    executeBatchedInvalidations();
  }, INVALIDATION_DEBOUNCE_MS);
}

/**
 * Execute all pending invalidations in a batched manner
 * Deduplicates query keys to avoid redundant refetches
 */
function executeBatchedInvalidations(): void {
  const batch = [...pendingInvalidations];
  pendingInvalidations = [];
  invalidationTimer = null;

  if (batch.length === 0) return;

  // Collect unique IDs to batch invalidations
  const projectIds = new Set<string>();
  const workspaceIds = new Set<string>();
  const entityTypes = new Set<string>();

  // Process all pending invalidations and collect what needs to be invalidated
  for (const inv of batch) {
    entityTypes.add(inv.entityType);
    if (inv.data?.projectId) projectIds.add(inv.data.projectId);
    if (inv.data?.previousProjectId) projectIds.add(inv.data.previousProjectId);
    if (inv.data?.workspaceId) workspaceIds.add(inv.data.workspaceId);
  }

  // Recents and search show every entity type but a run (below).
  //
  // NOTE on refetchType: every invalidation in this function deliberately
  // uses the default ('active'). 'all' also refetched INACTIVE cached
  // queries — during one navigation with a couple of socket events that
  // re-fired the same recents/search/hierarchy queries 2-3×, which browser
  // profiling showed was the single largest source of duplicate API calls.
  // Invalidation still marks inactive queries stale, so they refetch the
  // moment they're mounted again — same freshness, no background storm.
  // A Live protocol run (`protocol_run`) is in none of the generic lists
  // (recents, search, the sidebar hierarchy), and its writes (heartbeat
  // included, once a minute per open session) must not refetch them.
  const touchesNonRun = batch.some((inv) => inv.entityType !== PROTOCOL_RUN_ENTITY_TYPE);
  if (touchesNonRun) {
    queryClient.invalidateQueries({ queryKey: ['recents'] });
    queryClient.invalidateQueries({ queryKey: ['search'] });
  }

  // Handle project invalidations
  if (entityTypes.has('project')) {
    // Invalidate checklist — creating a project completes a checklist step
    queryClient.invalidateQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
    // Invalidate the collection lists for every workspace that had changes.
    for (const wsId of workspaceIds) {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.list(wsId) });
      // Workspace artifact lists carry each row's `parentCollectionName`, so a
      // renamed or trashed collection must refresh them too, or rows keep the
      // old name (or keep naming a collection that is now in the trash).
      queryClient.invalidateQueries({
        queryKey: ['artifacts', 'infinite', { workspaceId: wsId }],
      });
      // The suggested collections carry names and trashed state too: the splash
      // title and the Create modal's "Saving to" would keep a stale one.
      queryClient.invalidateQueries({ queryKey: queryKeys.purposeCollections(wsId) });
    }
    // Invalidate individual project details
    for (const inv of batch.filter((i) => i.entityType === 'project')) {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(inv.entityId) });
      if (inv.data?.parentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.children(inv.data.parentId) });
      }
      if (inv.data?.previousParentId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.children(inv.data.previousParentId),
        });
      }
    }
  }

  // Handle artifact invalidations
  const hasArtifactTypes = Array.from(entityTypes).some(isArtifactEntityType);
  if (hasArtifactTypes) {
    // Invalidate checklist — creating/uploading an artifact completes a checklist step
    queryClient.invalidateQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
    // Attachment bars are keyed by their parent artifact, while sync events
    // identify the changed child attachment. Invalidate the active prefix so
    // async OCR/title updates reach the mounted note without needing parent
    // context in every broadcast.
    queryClient.invalidateQueries({ queryKey: ['attachments'] });
    // Invalidate artifact lists for all affected projects
    for (const projId of projectIds) {
      queryClient.invalidateQueries({ queryKey: queryKeys.artifacts.all('project', projId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.children(projId) });
    }
    // Workspace Artifacts page uses `useInfiniteArtifacts` whose queryKey is
    // `['artifacts', 'infinite', { workspaceId }, filters]` — same prefix
    // mismatch as projects above; invalidate the infinite shape per workspace.
    for (const wsId of workspaceIds) {
      queryClient.invalidateQueries({ queryKey: ['artifacts', 'infinite', { workspaceId: wsId }] });
    }
    // Invalidate individual artifact details
    for (const inv of batch.filter((i) => isArtifactEntityType(i.entityType))) {
      queryClient.invalidateQueries({ queryKey: queryKeys.artifacts.detail(inv.entityId) });
      // Equipment details live on their own root too, and an equipment item IS
      // an artifact — a rename from the tree kebab arrives on this very branch.
      // The broad ['equipment'] sweep below is list-only now, so without this
      // line an open EquipmentDetail page keeps the stale title. No-op for the
      // artifact ids that aren't equipment.
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.detail(inv.entityId) });
      // The note summary panel caches the generated summary under its own key
      // (`useNoteSummary`); a summary generated by another user must reach it.
      queryClient.invalidateQueries({ queryKey: ['note-summary', inv.entityId] });
      // An open protocol page reads its steps here, and the agent's step tools
      // write them from chat or Live. Every version of this protocol; a no-op for
      // any other artifact. The step editor's write queue decides what a newer
      // revision does to text still being typed (stepWriteQueue.ts).
      queryClient.invalidateQueries({ queryKey: [...STRUCTURED_PROTOCOL_KEY, inv.entityId] });
    }
    // Equipment IS an artifact (artifactType = EQUIPMENT), but its LIST hooks
    // key on ['equipment', ...]. Without this, a rename/move/delete of an
    // equipment item from the tree kebab left the drawer's Equipment tab (and
    // the Equipment page) serving the stale title for its whole staleTime.
    // List-only — the changed item's own detail is handled by the loop above.
    queryClient.invalidateQueries({ queryKey: ['equipment'] });
    // A run record shows its notes' text in place (`RunAnalysisNote`), so a
    // run listing a changed or deleted capture refetches; other runs do not.
    const changedIds = new Set(
      batch.filter((i) => isArtifactEntityType(i.entityType)).map((i) => i.entityId),
    );
    queryClient.invalidateQueries({
      queryKey: ['protocol-run'],
      predicate: (query) => {
        const run = query.state.data as
          | { captures?: ReadonlyArray<{ artifact: { id: string } }> }
          | undefined;
        return run?.captures?.some((c) => changedIds.has(c.artifact.id)) ?? false;
      },
    });
    // Invalidate workspace-level note lists. (Two other keys were invalidated
    // here against no registered query: ['workspaceUploads'], which never had
    // one, and ['allArtifacts'], whose only producer — the `useAllArtifacts`
    // bridge hook — was deleted when ProtocolLibrary moved to
    // `useInfiniteArtifacts`. That page's rows now come from the
    // ['artifacts', 'infinite', { workspaceId }] sweep a few lines above.)
    for (const wsId of workspaceIds) {
      queryClient.invalidateQueries({ queryKey: ['workspaceNotes', { workspaceId: wsId }] });
    }
  }

  // Handle workspace invalidations
  if (entityTypes.has('workspace')) {
    // Invalidate checklist — workspace changes (e.g. invitations) can affect checklist steps
    queryClient.invalidateQueries({ queryKey: ONBOARDING_CHECKLIST_KEY });
    // Kept, and now list-only: `workspaces.all` IS the workspace-list key (the
    // switcher, Settings), which the per-entity loop below does NOT cover — it
    // only refreshes details. Before `detail` moved to its own root this same
    // line also refetched every cached workspace detail, duplicating the loop.
    queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all });
    for (const inv of batch.filter((i) => i.entityType === 'workspace')) {
      queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.detail(inv.entityId) });
    }
  }

  // Handle conversation invalidations
  if (entityTypes.has('conversation')) {
    for (const wsId of workspaceIds) {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all(wsId) });
    }
    for (const inv of batch.filter((i) => i.entityType === 'conversation')) {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(inv.entityId) });
    }
  }

  // Handle relationship invalidations — invalidate all linked-entity queries
  if (entityTypes.has('relationship')) {
    queryClient.invalidateQueries({ queryKey: ['linked-entities'] });
    // A captured_during edge landing: `artifact.linkedProtocol` drives a
    // capture's "during <Protocol>" pill and its session grouping, off the
    // ['artifacts','infinite',…] key. Not covered by the artifact branch: that
    // fires BEFORE the edge exists, and the two events do not reliably
    // coalesce inside INVALIDATION_DEBOUNCE_MS.
    for (const wsId of workspaceIds) {
      queryClient.invalidateQueries({ queryKey: ['artifacts', 'infinite', { workspaceId: wsId }] });
    }
    // ...and the same edge puts a note added elsewhere (Live, another tab)
    // on the run record that is open.
    queryClient.invalidateQueries({ queryKey: ['protocol-run'] });
  }

  // Handle user invalidations — profile edits arrive via chat or UI and
  // need to refresh the current-user profile + per-id user details so
  // every tab sees the new name / ORCID iD without a manual reload.
  // Workspace members lists also surface the updated display name.
  if (entityTypes.has('user')) {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.current });
    for (const inv of batch.filter((i) => i.entityType === 'user')) {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(inv.entityId) });
    }
    for (const wsId of workspaceIds) {
      queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.members(wsId) });
    }
  }

  invalidateWorkspaceItems(batch);

  // Queries waiting on a background job (derivation, imports, video insights,
  // onboarding sources): their only refresh now that none of them polls.
  invalidateJobStateQueries(queryClient, batch, isArtifactEntityType);

  // Always invalidate hierarchy if any entity type but a run changed
  if (touchesNonRun) {
    queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
  }
}

const rootOr = (id: string | null): string => id ?? WORKSPACE_ROOT_PARENT;

/**
 * The folder lists (`params.parent` values) a folder change touched: the
 * folder's parent now and, for a move, the one it left. `null` when the event
 * does not say, so the caller cannot target it.
 */
function folderParents(d: EntityChangeData | undefined): string[] | null {
  const current = d?.parentId;
  if (current === undefined) return null;
  const parents = [rootOr(current)];
  if (d?.previousParentId !== undefined) parents.push(rootOr(d.previousParentId));
  return parents;
}

/**
 * Same for a file: its parent now and the one it left. A file nested under
 * another artifact (an attachment in a note) is in no folder list, so it
 * names no list. `null` when the event does not say.
 */
function fileParents(d: EntityChangeData | undefined): string[] | null {
  if (!d) return null;
  const ends: Array<[string | undefined, string | null | undefined]> = [
    [d.parentEntityType, d.parentId],
    [d.previousParentEntityType, d.previousParentId],
  ];
  const parents: string[] = [];
  let named = false;
  for (const [type, id] of ends) {
    if (type === undefined) continue;
    named = true;
    if (type === 'workspace') parents.push(WORKSPACE_ROOT_PARENT);
    else if (type === 'project') {
      if (!id) return null;
      parents.push(id);
    }
  }
  // The upload path names only the folder (uploadManagerStore).
  if (!named && d.projectId) return [d.projectId];
  return named ? parents : null;
}

/**
 * The workspace items view. Per changed file or folder: the folder lists it
 * left and entered (every sort/filter variant of each, by partial match) and,
 * for a file, its card preview (a folder has none). A folder change also
 * stales every breadcrumb in the workspace, because each descendant's path
 * runs through it; only mounted ones refetch. An event that names no parent
 * sweeps that workspace's lists rather than leaving one silently stale.
 *
 * Two more reaches, because the parents an event names are not enough:
 * - A single-item move (project.move, artifact.update, the undo path) names
 *   only the NEW parent, so the list the item LEFT is found by what it holds:
 *   every cached list in that workspace whose pages contain the changed id.
 *   Only cached data is read; no request is made to find it.
 * - A `descendants` list is keyed on an ancestor, which no event names, so
 *   every descendants list in the workspace goes stale on any file or folder
 *   change. Only mounted ones refetch.
 */
function invalidateWorkspaceItems(batch: readonly PendingInvalidation[]): void {
  const keys = new Map<string, readonly unknown[]>();
  const add = (key: readonly unknown[]) => keys.set(JSON.stringify(key), key);
  // Changed ids per workspace scope, for the holds-the-item sweep.
  const changed = new Map<string, { scope: object; ids: Set<string> }>();
  for (const inv of batch) {
    const isFolder = inv.entityType === 'project';
    if (!isFolder && !isArtifactEntityType(inv.entityType)) continue;
    const scope = inv.data?.workspaceId ? { workspaceId: inv.data.workspaceId } : {};
    const scopeKey = JSON.stringify(scope);
    const entry = changed.get(scopeKey) ?? { scope, ids: new Set<string>() };
    entry.ids.add(inv.entityId);
    changed.set(scopeKey, entry);
    add(['workspace-items', scope, { scope: 'descendants' }]);
    if (isFolder) add(['folder-path', scope]);
    else add(queryKeys.itemPreview(inv.entityId));
    const parents = isFolder ? folderParents(inv.data) : fileParents(inv.data);
    if (parents === null) {
      add(['workspace-items', scope]);
      continue;
    }
    for (const parent of parents) {
      add(['workspace-items', scope, { parent }]);
    }
  }
  for (const queryKey of keys.values()) invalidateItemsKey(queryKey);
  for (const { scope, ids } of changed.values()) {
    refreshFolderLists(queryClient, {
      queryKey: ['workspace-items', scope],
      predicate: (query) => pagesHoldAny(query.state.data, ids),
    });
  }
}

/**
 * Folder lists refresh bounded (only down to what is on screen, never
 * restarting a refresh in flight); breadcrumbs and previews as usual.
 */
function invalidateItemsKey(queryKey: readonly unknown[]): void {
  if (queryKey[0] === 'workspace-items') refreshFolderLists(queryClient, { queryKey });
  else void queryClient.invalidateQueries({ queryKey });
}

/** Whether a cached workspace-items list has any of these ids on a loaded page. */
function pagesHoldAny(data: unknown, ids: ReadonlySet<string>): boolean {
  const pages = (data as { pages?: unknown } | null | undefined)?.pages;
  if (!Array.isArray(pages)) return false;
  return pages.some((page) => {
    const items = (page as { items?: unknown } | null)?.items;
    return (
      Array.isArray(items) && items.some((item) => ids.has((item as { id?: string })?.id ?? ''))
    );
  });
}

/**
 * Rows a cached artifact query holds, whatever its shape: a detail is one row,
 * project children are an array, and `useInfiniteArtifacts` pages carry
 * `items`. Anything else yields no rows.
 */
function cachedRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const pages = (data as { pages?: unknown }).pages;
  if (Array.isArray(pages)) {
    return pages.flatMap((p) => {
      const items = (p as { items?: unknown } | null)?.items;
      return Array.isArray(items) ? items : [];
    });
  }
  return [data];
}

const hasActiveTask = (row: unknown): boolean =>
  Object.values(
    readBackgroundTasks((row as { processingData?: unknown } | null)?.processingData),
  ).some((t) => isBackgroundTaskActive(t));

/**
 * Reconnect catch-up for background tasks whose terminal /sync event may have
 * been missed while the socket was down. Invalidates exactly the cached
 * artifact detail, artifact-list (project children, infinite and
 * parent-scoped artifact lists), and attachment-list queries that still show
 * a queued or running task, so a surface reached by reload or deep link, with
 * no activity-store entry, still recovers. `attachments` covers a note
 * reopened by reload with a busy transcription slot still in its cached
 * attachment bar — without it that row's live signal never arrives and no
 * refetch is scheduled either, so it is stuck until the bar's own staleTime
 * lapses. Event-driven (the reconnect), never polled. Returns the number of
 * queries invalidated.
 */
export function invalidateQueriesWithActiveTasks(): number {
  let count = 0;
  for (const query of queryClient.getQueryCache().getAll()) {
    const [root, second, third] = query.queryKey;
    const isArtifactQuery =
      root === 'artifact-detail' ||
      root === 'artifacts' ||
      root === 'attachments' ||
      (root === 'projects' && third === 'children' && typeof second === 'string');
    if (!isArtifactQuery) continue;
    if (!cachedRows(query.state.data).some(hasActiveTask)) continue;
    void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
    count += 1;
  }
  return count;
}

/**
 * Clear all queries for a workspace (used when switching workspaces)
 */
export function clearWorkspaceQueries(): void {
  queryClient.clear();
  // A selection made in the old workspace must not survive into the new one.
  useSelectionStore.getState().clear();
  // So is the canvas clipboard: it never pastes into another workspace.
  useCanvasClipboardStore.getState().clear();
}
